/**
 * sqlite3mt database adapter — bridges sqlite3mt sync engine lifecycle
 * to the public SqliteEngineDatabaseAdapter interface.
 *
 * §96 §2.2, §14.1, §15.9: The sqlite3mt runtime owns its own sync lifecycle
 * (push, reconcile, checkpoint). This adapter surfaces that lifecycle through
 * the standard adapter interface so RealSqliteDatabase can consume it
 * without knowing about sqlite3mt internals.
 *
 * The adapter observes ControlWorkerRuntimeState to derive diagnostics
 * and tracks sync cycles started/completed through the kickSyncEngine flow.
 */

import type { SqliteFlushLocalOptions, SyncDiag, SyncEvent, SyncMode, SyncResult } from '../sqliteDatabase'
import type { SqliteEngineDatabaseAdapter } from '../sqliteEngine'
import { emitSdkEvent, emitSdkMetric } from '../observers'
import { sdkConsole } from '../logger'
import { workspaceHasLocalDelta } from './dbview'
import type { ControlWorkerRuntimeState } from './lifecycle'
import type { SyncEngineKickReason } from './sync-engine'
import { sameRoot } from './types'

export interface Sqlite3MtAdapterState {
    /** Read-only view into the control worker runtime state. */
    readonly state: Readonly<ControlWorkerRuntimeState>
    /** SAB-backed edge: commit post published by client but not yet registered. */
    readonly hasOutstandingCommitPosts?: () => boolean
    /** SAB-backed count of connections currently holding SQLite WAL_WRITE. */
    readonly getWalWriteHolders?: () => number
}

function normalizeRuntimeError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error))
}

function waitOneTaskTurn(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0))
}

const FLUSH_REMOTE_REFRESH_MIN_INTERVAL_MS = 500
const FLUSH_WAIT_MAX_DELAY_MS = 25
const IDLE_WAIT_TIMEOUT_MS_ENV = 'SECUREBLOB_SQLITE3MT_IDLE_WAIT_TIMEOUT_MS'

function computeFlushWaitDelayMs(attempt: number): number {
    if (attempt <= 0) return 0
    return Math.min(2 ** Math.min(attempt - 1, 4), FLUSH_WAIT_MAX_DELAY_MS)
}

function readIdleWaitTimeoutMs(): number {
    const globalWithRuntimeEnv = globalThis as typeof globalThis & {
        __secureblobSqlite3MtIdleWaitTimeoutMs?: number | string
        __secureblobComponentJobRuntimeEnv?: Record<string, string | undefined>
        process?: { env?: Record<string, string | undefined> }
    }
    const raw = globalWithRuntimeEnv.__secureblobSqlite3MtIdleWaitTimeoutMs
        ?? globalWithRuntimeEnv.__secureblobComponentJobRuntimeEnv?.[IDLE_WAIT_TIMEOUT_MS_ENV]
        ?? globalWithRuntimeEnv.process?.env?.[IDLE_WAIT_TIMEOUT_MS_ENV]
    if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : 0
    if (typeof raw !== 'string' || raw.trim().length === 0) return 0

    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0
}

function createIdleWaitTimeoutError(
    operation: string,
    startedAt: number,
    timeoutMs: number,
    blockers: Record<string, unknown>,
): Error | null {
    if (timeoutMs <= 0 || Date.now() - startedAt < timeoutMs) return null

    const error = new Error(
        `Timed out waiting for sqlite3mt ${operation} to become idle after ${timeoutMs}ms. blockers=${compactTimeoutBlockers(blockers)}`,
    )
    ;(error as Error & { blockers?: Record<string, unknown> }).blockers = blockers
    return error
}

function compactTimeoutBlockers(blockers: Record<string, unknown>): string {
    try {
        return JSON.stringify(blockers)
    } catch {
        return '[unserializable]'
    }
}

function getRecentStageTraceSignature(state: Readonly<ControlWorkerRuntimeState>): string | null {
    const trace = (state as ControlWorkerRuntimeState & {
        __debugStageTrace?: Array<{
            kind?: unknown
            stage?: unknown
            remoteVersion?: unknown
            localVersion?: unknown
        }>
    }).__debugStageTrace
    if (!Array.isArray(trace) || trace.length === 0) return null

    const last = trace[trace.length - 1]
    return `${trace.length}:${String(last?.kind ?? '')}:${String(last?.stage ?? '')}:${String(last?.remoteVersion ?? '')}:${String(last?.localVersion ?? '')}`
}

function getIdleWaitProgressSignature(
    adapterState: Sqlite3MtAdapterState,
    remote: boolean,
): string {
    const state = adapterState.state
    const debugState = state as ControlWorkerRuntimeState & {
        __debugPushStage?: string | null
        __debugReconcileStage?: string | null
    }
    const base: Record<string, unknown> = {
        externalClientRequestCount: state.externalClientRequestCount,
        outstandingCommitPosts: hasOutstandingCommitPosts(adapterState),
        walWriteHolders: getWalWriteHolders(adapterState),
        pendingDbDeltas: state.pendingDbDeltas.size,
        checkpointBusy: state.checkpointBusy,
        checkpointQuiescentBusy: state.checkpointQuiescentBusy,
        checkpointWanted: state.checkpointWanted,
        inflightCommitRegistrations: state.inflightCommitRegistrations,
        snapshotPersistBusy: state.snapshotPersistBusy,
        snapshotPersistWanted: state.snapshotPersistWanted,
        stageTrace: getRecentStageTraceSignature(state),
    }

    if (remote) {
        base.pushBusy = state.pushBusy
        base.mergeBusy = state.mergeBusy
        base.compactBusy = state.compactBusy
        base.compactWanted = state.compactWanted
        base.pendingBaseAdvance = state.pendingBaseAdvance?.kind ?? null
        base.reconcileIntent = state.reconcileIntent?.phase ?? null
        base.headAdvancePreparing = state.headAdvancePreparing
        base.syncRetryScheduled = state.syncRetryScheduled
        base.workspaceOverlaySize = state.workspaceOverlay.size
        base.workspacePageCount = state.workspacePageCount
        base.workspaceBasePageCount = state.workspaceBaseHead.root.meta.pageCount
        base.remoteRootId = state.remoteHead.root.rootId
        base.workspaceBaseRootId = state.workspaceBaseHead.root.rootId
        base.remoteVersion = state.remoteHead.version.toString()
        base.workspaceBaseVersion = state.workspaceBaseHead.version.toString()
        base.pushStage = debugState.__debugPushStage ?? null
        base.reconcileStage = debugState.__debugReconcileStage ?? null
        base.integratedFrameMax = state.integratedFrameMax
    }

    return JSON.stringify(base)
}

async function waitForSnapshotPersistTail(
    state: Readonly<ControlWorkerRuntimeState>,
    operation: string,
    startedAt: number,
    timeoutMs: number,
    blockers: Record<string, unknown>,
): Promise<void> {
    if (timeoutMs <= 0) {
        await state.snapshotPersistTail
        return
    }

    const remainingMs = Math.max(0, timeoutMs - (Date.now() - startedAt))
    const timeoutError = createIdleWaitTimeoutError(
        operation,
        remainingMs === 0 ? startedAt - timeoutMs : Date.now() - timeoutMs,
        timeoutMs,
        { ...blockers, snapshotPersistTail: true },
    )
    if (remainingMs === 0 && timeoutError != null) {
        throw timeoutError
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    try {
        await Promise.race([
            state.snapshotPersistTail,
            new Promise<never>((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(
                        `Timed out waiting for sqlite3mt ${operation} snapshot persist tail after ${timeoutMs}ms. blockers=${compactTimeoutBlockers({ ...blockers, snapshotPersistTail: true })}`,
                    ))
                }, remainingMs)
            }),
        ])
    } catch (error) {
        const normalized = normalizeRuntimeError(error)
        ;(normalized as Error & { blockers?: Record<string, unknown> }).blockers = {
            ...blockers,
            snapshotPersistTail: true,
        }
        throw normalized
    } finally {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId)
        }
    }
}

function hasOutstandingCommitPosts(adapterState: Sqlite3MtAdapterState): boolean {
    return adapterState.hasOutstandingCommitPosts?.() === true
}

function getWalWriteHolders(adapterState: Sqlite3MtAdapterState): number {
    return Math.max(0, adapterState.getWalWriteHolders?.() ?? 0)
}

function shouldRequestRemoteRefresh(adapterState: Sqlite3MtAdapterState): boolean {
    const state = adapterState.state
    return (
        !state.pushBusy
        && state.pendingBaseAdvance == null
        && !state.headAdvancePreparing
        && state.inflightCommitRegistrations === 0
    )
}

function requestRemoteRefreshBestEffort(
    requestRemoteRefresh: (() => Promise<void> | void) | undefined,
): void {
    if (requestRemoteRefresh == null) {
        return
    }

    void (async () => {
        try {
            await requestRemoteRefresh()
        } catch (error) {
            sdkConsole.warn('[sqlite3mt:adapter] remote refresh failed', {
                error: error instanceof Error ? error.message : String(error),
            })
        }
    })()
}

function remoteHeadChangedSince(
    adapterState: Sqlite3MtAdapterState,
    initialRemoteRootId: string,
    initialRemoteVersion: bigint,
    initialBaseRootId: string,
    initialBaseVersion: bigint,
): boolean {
    const state = adapterState.state
    return state.remoteHead.root.rootId !== initialRemoteRootId
        || state.remoteHead.version !== initialRemoteVersion
        || state.workspaceBaseHead.root.rootId !== initialBaseRootId
        || state.workspaceBaseHead.version !== initialBaseVersion
}

function snapshotPublicHead(head: { root: { rootId: string }; version: bigint }): { rootId: string; version: bigint } {
    return { rootId: head.root.rootId, version: head.version }
}

function publicHeadMatchesSnapshot(
    head: { root: { rootId: string }; version: bigint },
    snapshot: { rootId: string; version: bigint },
): boolean {
    return head.root.rootId === snapshot.rootId && head.version === snapshot.version
}

function hasWorkspaceLocalDelta(adapterState: Sqlite3MtAdapterState): boolean {
    const state = adapterState.state
    return workspaceHasLocalDelta(
        state.workspaceOverlay,
        state.workspacePageCount,
        state.workspaceBaseHead.root.meta.pageCount,
    )
}

let flushWaitSeq = 0

function isLocalFlushIdle(adapterState: Sqlite3MtAdapterState): boolean {
    const state = adapterState.state

    return state.externalClientRequestCount === 0
        && !hasOutstandingCommitPosts(adapterState)
        && getWalWriteHolders(adapterState) === 0
        && state.pendingDbDeltas.size === 0
        && !state.checkpointBusy
        && !state.checkpointQuiescentBusy
        && !state.checkpointWanted
        && state.inflightCommitRegistrations === 0
        && !state.snapshotPersistBusy
        && !state.snapshotPersistWanted
}

function isRemoteFlushIdle(adapterState: Sqlite3MtAdapterState): boolean {
    const state = adapterState.state

    return isLocalFlushIdle(adapterState)
        && !state.pushBusy
        && !state.mergeBusy
        && !state.compactBusy
        && !state.compactWanted
        && state.pendingBaseAdvance == null
        && state.reconcileIntent == null
        && !state.headAdvancePreparing
        && state.inflightCommitRegistrations === 0
        && !workspaceHasLocalDelta(
            state.workspaceOverlay,
            state.workspacePageCount,
            state.workspaceBaseHead.root.meta.pageCount
        )
        && sameRoot(state.remoteHead.root, state.workspaceBaseHead.root)
        && state.remoteHead.version === state.workspaceBaseHead.version
}

function shouldKickLocalFlushMaintenance(adapterState: Sqlite3MtAdapterState): boolean {
    const state = adapterState.state
    if (state.snapshotPersistBusy) {
        return state.externalClientRequestCount !== 0
            || hasOutstandingCommitPosts(adapterState)
            || getWalWriteHolders(adapterState) !== 0
            || state.pendingDbDeltas.size !== 0
            || state.checkpointBusy
            || state.checkpointQuiescentBusy
            || state.checkpointWanted
            || state.inflightCommitRegistrations !== 0
    }

    return !isLocalFlushIdle(adapterState)
}

function getFlushBlockers(
    adapterState: Sqlite3MtAdapterState,
    remote: boolean,
): Record<string, unknown> {
    const state = adapterState.state
    const blockers: Record<string, unknown> = {}
    const hasLocalDelta = workspaceHasLocalDelta(
        state.workspaceOverlay,
        state.workspacePageCount,
        state.workspaceBaseHead.root.meta.pageCount,
    )

    if (state.externalClientRequestCount !== 0) blockers.externalClientRequestCount = state.externalClientRequestCount
    if (hasOutstandingCommitPosts(adapterState)) blockers.outstandingCommitPosts = true
    const walWriteHolders = getWalWriteHolders(adapterState)
    if (walWriteHolders !== 0) blockers.walWriteHolders = walWriteHolders
    if (state.pendingDbDeltas.size !== 0) blockers.pendingDbDeltas = state.pendingDbDeltas.size
    if (remote) {
        if (state.pushBusy) {
            blockers.pushBusy = true
            const pushStage = (state as typeof state & { __debugPushStage?: string | null }).__debugPushStage
            if (pushStage) blockers.pushStage = pushStage
        }
        if (state.mergeBusy) blockers.mergeBusy = true
        if (state.checkpointBusy) blockers.checkpointBusy = true
        if (state.checkpointQuiescentBusy) blockers.checkpointQuiescentBusy = true
        if (state.compactBusy) blockers.compactBusy = true
        if (state.compactWanted) blockers.compactWanted = true
        if (state.checkpointWanted) blockers.checkpointWanted = true
        if (state.pendingBaseAdvance != null) blockers.pendingBaseAdvance = state.pendingBaseAdvance.kind
        if (state.reconcileIntent != null) blockers.reconcileIntent = state.reconcileIntent.phase
        if (state.headAdvancePreparing) blockers.headAdvancePreparing = true
        if (state.inflightCommitRegistrations !== 0) blockers.inflightCommitRegistrations = state.inflightCommitRegistrations
        if (state.snapshotPersistBusy) blockers.snapshotPersistBusy = true
        if (state.snapshotPersistWanted) blockers.snapshotPersistWanted = true
        if (state.syncRetryScheduled) blockers.syncRetryScheduled = true
    } else {
        if (state.checkpointBusy) blockers.checkpointBusy = true
        if (state.checkpointQuiescentBusy) blockers.checkpointQuiescentBusy = true
        if (state.checkpointWanted) blockers.checkpointWanted = true
        if (state.inflightCommitRegistrations !== 0) blockers.inflightCommitRegistrations = state.inflightCommitRegistrations
        if (state.snapshotPersistBusy) blockers.snapshotPersistBusy = true
        if (state.snapshotPersistWanted) blockers.snapshotPersistWanted = true
    }

    if (remote) {
        if (hasLocalDelta) {
            blockers.workspaceOverlaySize = state.workspaceOverlay.size
            blockers.workspacePageCount = state.workspacePageCount
            blockers.workspaceBasePageCount = state.workspaceBaseHead.root.meta.pageCount
        }
        if (!sameRoot(state.remoteHead.root, state.workspaceBaseHead.root)) {
            blockers.remoteRootId = state.remoteHead.root.rootId
            blockers.workspaceBaseRootId = state.workspaceBaseHead.root.rootId
        }
        if (state.remoteHead.version !== state.workspaceBaseHead.version) {
            blockers.remoteVersion = state.remoteHead.version.toString()
            blockers.workspaceBaseVersion = state.workspaceBaseHead.version.toString()
        }
    }

    if (state.lastSyncError != null) {
        blockers.lastSyncError = state.lastSyncError instanceof Error
            ? state.lastSyncError.message
            : String(state.lastSyncError)
    }

    return blockers
}

function hasPendingSyncRecovery(adapterState: Sqlite3MtAdapterState): boolean {
    const state = adapterState.state
    return state.syncRetryScheduled
        || state.pushBusy
        || state.mergeBusy
        || state.pendingBaseAdvance != null
        || state.reconcileIntent != null
        || state.headAdvancePreparing
        || state.inflightCommitRegistrations !== 0
        || hasOutstandingCommitPosts(adapterState)
        || workspaceHasLocalDelta(
            state.workspaceOverlay,
            state.workspacePageCount,
            state.workspaceBaseHead.root.meta.pageCount,
        )
        || !sameRoot(state.remoteHead.root, state.workspaceBaseHead.root)
        || state.remoteHead.version !== state.workspaceBaseHead.version
}

async function flushUntilIdle(
    adapterState: Sqlite3MtAdapterState,
    kickSyncEngine: (reason: SyncEngineKickReason) => void,
    lastErrorRef: { current: Error | null },
    remote: boolean,
    stopped: { current: boolean },
    requestRemoteRefresh?: () => Promise<void> | void,
): Promise<void> {
    const flushId = ++flushWaitSeq
    if (stopped.current) {
        stopped.current = false
    }

    const flushReason = remote ? 'adapter-flush-remote' : 'adapter-flush-local'
    const startedAt = Date.now()
    const timeoutMs = readIdleWaitTimeoutMs()
    let lastProgressAt = startedAt
    let lastProgressSignature = getIdleWaitProgressSignature(adapterState, remote)
    let lastLoggedAt = 0
    let loopCount = 0
    let lastRemoteRefreshAt = 0
    const initialRemoteRootId = adapterState.state.remoteHead.root.rootId
    const initialRemoteVersion = adapterState.state.remoteHead.version
    const initialBaseRootId = adapterState.state.workspaceBaseHead.root.rootId
    const initialBaseVersion = adapterState.state.workspaceBaseHead.version
    const mustVerifyInitiallyConvergedRemote =
        remote
        && requestRemoteRefresh != null
        && isRemoteFlushIdle(adapterState)
    let initialRemoteRefreshStarted = false
    let initialRemoteRefreshSettled = !mustVerifyInitiallyConvergedRemote
    let initialRemoteRefreshError: Error | null = null

    const startInitialRemoteRefresh = (): void => {
        if (!mustVerifyInitiallyConvergedRemote || initialRemoteRefreshStarted) {
            return
        }
        initialRemoteRefreshStarted = true
        lastRemoteRefreshAt = Date.now()
        sdkConsole.debug('[sqlite3mt:adapter] flushRemote verifying published head before idle', {
            flushId,
            remoteVersion: initialRemoteVersion.toString(),
            workspaceBaseVersion: initialBaseVersion.toString(),
            remoteRootId: initialRemoteRootId,
            workspaceBaseRootId: initialBaseRootId,
        })
        void (async () => {
            try {
                await requestRemoteRefresh?.()
            } catch (error) {
                initialRemoteRefreshError = normalizeRuntimeError(error)
            } finally {
                initialRemoteRefreshSettled = true
            }
        })()
    }

    for (;;) {
        const state = adapterState.state

        if (state.syncFatalError != null) {
            const error = normalizeRuntimeError(state.syncFatalError)
            lastErrorRef.current = error
            throw error
        }

        if (remote && state.lastSyncError != null && !hasPendingSyncRecovery(adapterState)) {
            const error = normalizeRuntimeError(state.lastSyncError)
            lastErrorRef.current = error
            throw error
        }

        if (remote) {
            startInitialRemoteRefresh()
        }

        const remoteVerified =
            !mustVerifyInitiallyConvergedRemote
            || initialRemoteRefreshSettled
            || remoteHeadChangedSince(
                adapterState,
                initialRemoteRootId,
                initialRemoteVersion,
                initialBaseRootId,
                initialBaseVersion,
            )
        const idle = remote
            ? isRemoteFlushIdle(adapterState) && remoteVerified
            : isLocalFlushIdle(adapterState)
        if (idle) {
            sdkConsole.trace('[sqlite3mt:adapter] flush idle-candidate', {
                flushId,
                remote,
                remoteVerified,
            })
            if (!remote) {
                try {
                    await waitForSnapshotPersistTail(
                        state,
                        flushReason,
                        startedAt,
                        timeoutMs,
                        getFlushBlockers(adapterState, remote),
                    )
                } catch (errorSource) {
                    const error = normalizeRuntimeError(errorSource)
                    lastErrorRef.current = error
                    throw error
                }
                sdkConsole.trace('[sqlite3mt:adapter] flush snapshot-tail-settled', {
                    flushId,
                    remote,
                })

                if (adapterState.state.syncFatalError != null) {
                    const error = normalizeRuntimeError(adapterState.state.syncFatalError)
                    lastErrorRef.current = error
                    throw error
                }

                if (isLocalFlushIdle(adapterState)) {
                    sdkConsole.debug('[sqlite3mt:adapter] flush complete', {
                        flushId,
                        remote,
                    })
                    lastErrorRef.current = null
                    return
                }
            } else {
                sdkConsole.debug('[sqlite3mt:adapter] flush complete', {
                    flushId,
                    remote,
                    remoteVerified,
                })
                lastErrorRef.current = null
                return
            }
        } else if (remote && adapterState.state.lastSyncError == null && isRemoteFlushIdle(adapterState)) {
            if (
                mustVerifyInitiallyConvergedRemote
                && initialRemoteRefreshError != null
                && !remoteHeadChangedSince(
                    adapterState,
                    initialRemoteRootId,
                    initialRemoteVersion,
                    initialBaseRootId,
                    initialBaseVersion,
                )
            ) {
                lastErrorRef.current = initialRemoteRefreshError
                throw initialRemoteRefreshError
            }
            sdkConsole.debug('[sqlite3mt:adapter] flush complete', {
                flushId,
                remote,
                remoteVerified,
            })
            lastErrorRef.current = null
            return
        }

        const now = Date.now()
        const blockers = getFlushBlockers(adapterState, remote)
        const progressSignature = getIdleWaitProgressSignature(adapterState, remote)
        if (progressSignature !== lastProgressSignature) {
            lastProgressSignature = progressSignature
            lastProgressAt = now
        }
        const timeoutError = createIdleWaitTimeoutError(flushReason, lastProgressAt, timeoutMs, blockers)
        if (timeoutError != null) {
            lastErrorRef.current = timeoutError
            throw timeoutError
        }

        if (now - lastLoggedAt >= 1000) {
            lastLoggedAt = now
            sdkConsole.warn('[sqlite3mt:adapter] flush waiting', {
                remote,
                reason: flushReason,
                blockers,
            })
        }

        if (remote) {
            const shouldRefresh =
                shouldRequestRemoteRefresh(adapterState)
                && !state.mergeBusy
                && (
                    (loopCount === 0 && !initialRemoteRefreshStarted)
                    || now - lastRemoteRefreshAt >= FLUSH_REMOTE_REFRESH_MIN_INTERVAL_MS
                )
            if (shouldRefresh) {
                lastRemoteRefreshAt = now
                // Remote-head refresh is an accelerator, not a correctness
                // precondition for explicit flush completion. The runtime may
                // already converge via background sync or cross-worker head
                // notifications while a refresh fetch is still in flight, so
                // never let that helper pin flushRemote() indefinitely.
                requestRemoteRefreshBestEffort(requestRemoteRefresh)
            }
        }
        if (remote || shouldKickLocalFlushMaintenance(adapterState)) {
            kickSyncEngine(flushReason)
        }
        const waitDelayMs = computeFlushWaitDelayMs(loopCount)
        loopCount += 1
        if (waitDelayMs === 0) {
            await waitOneTaskTurn()
        } else {
            await new Promise<void>(resolve => setTimeout(resolve, waitDelayMs))
        }
    }
}

async function requestLocalFlush(
    adapterState: Sqlite3MtAdapterState,
    _kickSyncEngine: (reason: SyncEngineKickReason) => void,
    lastErrorRef: { current: Error | null },
    stopped: { current: boolean },
): Promise<void> {
    if (stopped.current) {
        stopped.current = false
    }
    if (adapterState.state.syncFatalError != null) {
        const error = normalizeRuntimeError(adapterState.state.syncFatalError)
        lastErrorRef.current = error
        throw error
    }
    if (adapterState.state.syncFatalError != null) {
        const error = normalizeRuntimeError(adapterState.state.syncFatalError)
        lastErrorRef.current = error
        throw error
    }
}

async function syncUntilIdle(
    adapterState: Sqlite3MtAdapterState,
    kickSyncEngine: (reason: SyncEngineKickReason) => void,
    lastErrorRef: { current: Error | null },
    requestRemoteRefresh: (() => Promise<void> | void) | undefined,
    stopped: { current: boolean },
): Promise<void> {
    if (stopped.current) {
        stopped.current = false
    }

    if (requestRemoteRefresh != null && shouldRequestRemoteRefresh(adapterState)) {
        await requestRemoteRefresh()
    }

    kickSyncEngine('adapter-sync-now')
    await waitOneTaskTurn()

    const startedAt = Date.now()
    const timeoutMs = readIdleWaitTimeoutMs()
    let lastProgressAt = startedAt
    let lastProgressSignature = getIdleWaitProgressSignature(adapterState, true)
    let loopCount = 0
    let lastRemoteRefreshAt = 0
    let lastLoggedAt = 0
    for (;;) {
        const state = adapterState.state

        if (state.syncFatalError != null) {
            const error = normalizeRuntimeError(state.syncFatalError)
            lastErrorRef.current = error
            throw error
        }

        if (state.lastSyncError != null) {
            const error = normalizeRuntimeError(state.lastSyncError)
            lastErrorRef.current = error
            throw error
        }

        const idle = isRemoteFlushIdle(adapterState)

        if (idle) {
            await waitForSnapshotPersistTail(
                state,
                'adapter-sync-now',
                startedAt,
                timeoutMs,
                getFlushBlockers(adapterState, true),
            )
            if (
                adapterState.state.syncFatalError == null
                && adapterState.state.lastSyncError == null
                && isRemoteFlushIdle(adapterState)
            ) {
                lastErrorRef.current = null
                return
            }
        }

        const now = Date.now()
        const blockers = getFlushBlockers(adapterState, true)
        const progressSignature = getIdleWaitProgressSignature(adapterState, true)
        if (progressSignature !== lastProgressSignature) {
            lastProgressSignature = progressSignature
            lastProgressAt = now
        }
        const timeoutError = createIdleWaitTimeoutError('adapter-sync-now', lastProgressAt, timeoutMs, blockers)
        if (timeoutError != null) {
            lastErrorRef.current = timeoutError
            throw timeoutError
        }

        if (now - lastLoggedAt >= 1000) {
            lastLoggedAt = now
            sdkConsole.warn('[sqlite3mt:adapter] syncNow waiting', {
                blockers,
            })
        }

        if (
            requestRemoteRefresh != null
            && shouldRequestRemoteRefresh(adapterState)
            && now - lastRemoteRefreshAt >= FLUSH_REMOTE_REFRESH_MIN_INTERVAL_MS
        ) {
            lastRemoteRefreshAt = now
            requestRemoteRefreshBestEffort(requestRemoteRefresh)
        }

        kickSyncEngine('adapter-sync-now')
        const waitDelayMs = computeFlushWaitDelayMs(loopCount)
        loopCount += 1
        if (waitDelayMs === 0) {
            await waitOneTaskTurn()
        } else {
            await new Promise<void>(resolve => setTimeout(resolve, waitDelayMs))
        }
    }
}

/**
 * Create a sqlite3mt-specific database adapter.
 *
 * The adapter does NOT own the sync lifecycle — it observes
 * ControlWorkerRuntimeState and translates state transitions into
 * SyncEvent callbacks for the public API.
 *
 * The kickSyncEngine callback triggers sync passes when the public API
 * calls startSync() or syncNow().
 */
export function createSqlite3MtDatabaseAdapter(
    adapterState: Sqlite3MtAdapterState,
    kickSyncEngine: (reason: SyncEngineKickReason) => void,
    options?: {
        requestRemoteRefresh?: () => Promise<void> | void
        requestDurableCachePrune?: () => Promise<void> | void
    },
): SqliteEngineDatabaseAdapter {
    let mode: SyncMode = 'concurrent' as SyncMode
    let cycleCount = 0
    let lastResult: SyncResult | null = null
    let lastError: Error | null = null
    let lastSyncAt: number | null = null
    const stopped = { current: false }
    const lastErrorRef: { current: Error | null } = { current: lastError }

    const listeners = new Set<(event: SyncEvent) => void>()

    const emit = (event: SyncEvent): void => {
        for (const listener of listeners) {
            try {
                listener(event)
            } catch {
                // Listener failures must not break the adapter event stream.
            }
        }
    }

    const emitObserverEvent = (
        level: 'debug' | 'info' | 'warn' | 'error',
        message: string,
        data?: unknown,
    ): void => {
        emitSdkEvent(adapterState.state.sdkObservers, {
            domain: 'sync',
            level,
            message,
            data,
        })
    }

    const emitObserverMetric = (
        name: string,
        value: number,
        tags?: Record<string, string>,
    ): void => {
        emitSdkMetric(adapterState.state.sdkObservers, name, value, tags)
    }

    return {
        async setSyncMode(nextMode: SyncMode): Promise<void> {
            const previousMode = mode
            mode = nextMode
            if (previousMode !== nextMode) {
                emit({
                    type: 'mode-change',
                    from: previousMode,
                    to: nextMode,
                    reason: 'manual',
                })
            }
        },

        async startSync(): Promise<void> {
            stopped.current = false
            kickSyncEngine('adapter-start-sync')
        },

        async stopSync(): Promise<void> {
            stopped.current = true
        },

        async syncNow(): Promise<SyncResult> {
            if (stopped.current) {
                stopped.current = false
            }

            const startedAt = Date.now()
            const initialBaseHead = snapshotPublicHead(adapterState.state.workspaceBaseHead)
            const initialRemoteHead = snapshotPublicHead(adapterState.state.remoteHead)
            const initialPageCount = adapterState.state.workspaceBaseHead.root.meta.pageCount
            const hadLocalDeltaAtStart = hasWorkspaceLocalDelta(adapterState)
            const initialOverlaySize = adapterState.state.workspaceOverlay.size
            const hadPushBusyAtStart = adapterState.state.pushBusy
            const hadMergeBusyAtStart = adapterState.state.mergeBusy
            const hadRemoteDivergenceAtStart =
                adapterState.state.remoteHead.root.rootId !== adapterState.state.workspaceBaseHead.root.rootId
                || adapterState.state.remoteHead.version !== adapterState.state.workspaceBaseHead.version

            if (adapterState.state.syncFatalError != null) {
                const error = adapterState.state.syncFatalError instanceof Error
                    ? adapterState.state.syncFatalError
                    : new Error(String(adapterState.state.syncFatalError))
                lastError = error
                lastErrorRef.current = error
                const result: SyncResult = {
                    direction: 'no-op',
                    pagesChanged: 0,
                    conflicts: [],
                    durationMs: Math.max(0, Date.now() - startedAt),
                }
                emit({ type: 'sync-error', error })
                emitObserverEvent('error', 'sqlite3mt sync failed before dispatch.', {
                    reason: 'syncFatalError',
                    error: error.message,
                })
                return result
            }

            try {
                await options?.requestDurableCachePrune?.()
                await syncUntilIdle(
                    adapterState,
                    kickSyncEngine,
                    lastErrorRef,
                    options?.requestRemoteRefresh,
                    stopped,
                )
                lastError = lastErrorRef.current
            } catch (errorSource) {
                const error = normalizeRuntimeError(errorSource)
                lastError = error
                lastErrorRef.current = error
                emit({ type: 'sync-error', error })
                emitObserverEvent('error', 'sqlite3mt sync failed during syncNow.', {
                    error: error.message,
                })
                return {
                    direction: 'no-op',
                    pagesChanged: 0,
                    conflicts: [],
                    durationMs: Math.max(0, Date.now() - startedAt),
                }
            }

            if (adapterState.state.syncFatalError != null || adapterState.state.lastSyncError != null) {
                const errorSource = adapterState.state.syncFatalError ?? adapterState.state.lastSyncError
                const error = errorSource instanceof Error
                    ? errorSource
                    : new Error(String(errorSource))
                lastError = error
                lastErrorRef.current = error
                emit({ type: 'sync-error', error })
                emitObserverEvent('error', 'sqlite3mt sync failed after dispatch.', {
                    reason: adapterState.state.syncFatalError != null ? 'syncFatalError' : 'lastSyncError',
                    error: error.message,
                })
                return {
                    direction: 'no-op',
                    pagesChanged: 0,
                    conflicts: [],
                    durationMs: Math.max(0, Date.now() - startedAt),
                }
            }

            const durationMs = Math.max(0, Date.now() - startedAt)
            const overlay = adapterState.state.workspaceOverlay
            const finalBaseHead = adapterState.state.workspaceBaseHead
            const finalRemoteHead = adapterState.state.remoteHead
            const baseAdvanced = !publicHeadMatchesSnapshot(finalBaseHead, initialBaseHead)
            const remoteAdvanced = !publicHeadMatchesSnapshot(finalRemoteHead, initialRemoteHead)
            const remoteApplied =
                baseAdvanced
                && (
                    hadRemoteDivergenceAtStart
                    || (!hadLocalDeltaAtStart && remoteAdvanced)
                )
            const pagesChanged = remoteApplied
                ? Math.max(1, Math.abs(finalBaseHead.root.meta.pageCount - initialPageCount) || overlay.size)
                : hadLocalDeltaAtStart
                    ? Math.max(1, initialOverlaySize)
                : overlay.size

            let direction: SyncResult['direction'] = 'no-op'
            if (remoteApplied && hadLocalDeltaAtStart) {
                direction = 'merge'
            } else if (remoteApplied) {
                direction = 'pull'
            } else if (hadPushBusyAtStart || adapterState.state.pushBusy) {
                direction = 'push'
            } else if (hadMergeBusyAtStart || adapterState.state.mergeBusy) {
                direction = 'merge'
            } else if (hadLocalDeltaAtStart || pagesChanged > 0) {
                direction = 'push'
            }

            const result: SyncResult = {
                direction,
                pagesChanged,
                conflicts: [],
                durationMs,
            }

            cycleCount += 1
            lastResult = result
            lastSyncAt = Date.now()
            if (remoteApplied) {
                emit({ type: 'remote-changes', changedPages: pagesChanged })
            }
            emit({ type: 'sync-complete', result })
            emitObserverEvent(direction === 'no-op' ? 'debug' : 'info', 'sqlite3mt sync completed.', {
                direction,
                pagesChanged,
                conflicts: result.conflicts.length,
                durationMs,
            })
            emitObserverMetric('sdk.sqlite.sync.duration_ms', durationMs, {
                direction,
            })

            return result
        },

        async flushLocal(flushOptions?: SqliteFlushLocalOptions): Promise<void> {
            try {
                if (flushOptions?.wait === false) {
                    await requestLocalFlush(adapterState, kickSyncEngine, lastErrorRef, stopped)
                    return
                }
                if (adapterState.state.checkpointWanted || adapterState.state.pendingDbDeltas.size > 0) {
                    kickSyncEngine('adapter-flush-local')
                }
                await options?.requestDurableCachePrune?.()
                await flushUntilIdle(adapterState, kickSyncEngine, lastErrorRef, false, stopped)
            } finally {
                lastError = lastErrorRef.current
            }
        },

        async flushRemote(): Promise<void> {
            try {
                await options?.requestDurableCachePrune?.()
                await flushUntilIdle(
                    adapterState,
                    kickSyncEngine,
                    lastErrorRef,
                    true,
                    stopped,
                    options?.requestRemoteRefresh,
                )
            } finally {
                lastError = lastErrorRef.current
            }
        },

        onSync(callback: (event: SyncEvent) => void): () => void {
            listeners.add(callback)
            return () => {
                listeners.delete(callback)
            }
        },

        getSyncDiagnosticsSnapshot(): Partial<SyncDiag> {
            const state = adapterState.state

            let syncState: SyncDiag['state'] = 'idle'
            if (state.syncFatalError != null) {
                syncState = 'error'
            } else if (state.lastSyncError != null) {
                syncState = 'error'
            } else if (lastErrorRef.current != null) {
                syncState = 'error'
            } else if (stopped.current) {
                syncState = 'stopped'
            } else if (state.checkpointBusy || state.checkpointQuiescentBusy) {
                syncState = 'checkpointing'
            } else if (state.mergeBusy) {
                syncState = 'merging'
            } else if (state.pushBusy) {
                syncState = 'pushing'
            }

            const snapshot: Partial<SyncDiag> & { debugInternal?: Record<string, unknown> } = {
                mode,
                state: syncState,
                isSyncing: state.pushBusy || state.mergeBusy,
                cycleCount,
                lastResult,
                lastError: state.syncFatalError instanceof Error
                    ? state.syncFatalError
                    : state.lastSyncError instanceof Error
                        ? state.lastSyncError
                        : lastErrorRef.current,
                lastSyncAt,
                pendingPushPages: state.workspaceOverlay.size,
                remoteEpoch: Number(state.remoteHead.version),
                localEpoch: Number(state.workspaceBaseHead.version),
            }

            snapshot.debugInternal = {
                pushBusy: state.pushBusy,
                mergeBusy: state.mergeBusy,
                checkpointBusy: state.checkpointBusy,
                checkpointQuiescentBusy: state.checkpointQuiescentBusy,
                compactBusy: state.compactBusy,
                compactWanted: state.compactWanted,
                checkpointWanted: state.checkpointWanted,
                externalClientRequestCount: state.externalClientRequestCount,
                outstandingCommitPosts: hasOutstandingCommitPosts(adapterState),
                walWriteHolders: getWalWriteHolders(adapterState),
                pendingBaseAdvanceKind: state.pendingBaseAdvance?.kind ?? null,
                reconcileIntentPhase: state.reconcileIntent?.phase ?? null,
                inflightCommitRegistrations: state.inflightCommitRegistrations,
                headAdvancePreparing: state.headAdvancePreparing,
                snapshotPersistBusy: state.snapshotPersistBusy,
                snapshotPersistWanted: state.snapshotPersistWanted,
                syncRetryScheduled: state.syncRetryScheduled,
                workspaceOverlaySize: state.workspaceOverlay.size,
                workspacePageCount: state.workspacePageCount,
                workspaceBasePageCount: state.workspaceBaseHead.root.meta.pageCount,
                integratedFrameMax: state.integratedFrameMax,
                pendingDbDeltaCount: state.pendingDbDeltas.size,
                currentWalSalt0: state.currentWalSalt0,
                currentWalSalt1: state.currentWalSalt1,
                pushStage: (state as ControlWorkerRuntimeState & { __debugPushStage?: string }).__debugPushStage ?? null,
                reconcileStage: (state as ControlWorkerRuntimeState & { __debugReconcileStage?: string }).__debugReconcileStage ?? null,
                lastCommitSync: (state as ControlWorkerRuntimeState & {
                    __debugLastCommitSync?: Record<string, unknown>
                }).__debugLastCommitSync ?? null,
                externalClientRequests: (state as ControlWorkerRuntimeState & {
                    __debugExternalClientRequests?: Record<string, unknown>
                }).__debugExternalClientRequests ?? null,
                sab4Scalars: (state as ControlWorkerRuntimeState & {
                    __debugSab4ScalarsGetter?: (() => Record<string, number>) | undefined
                }).__debugSab4ScalarsGetter?.() ?? null,
                stageTrace: (state as ControlWorkerRuntimeState & {
                    __debugStageTrace?: unknown
                }).__debugStageTrace ?? null,
                pushAbortTrace: (state as ControlWorkerRuntimeState & {
                    __debugPushAbortTrace?: unknown
                }).__debugPushAbortTrace ?? null,
                checkpointDispatch: (state as ControlWorkerRuntimeState & {
                    __debugCheckpointDispatch?: unknown
                }).__debugCheckpointDispatch ?? null,
                checkpointLifecycle: (state as ControlWorkerRuntimeState & {
                    __debugCheckpointLifecycle?: unknown
                }).__debugCheckpointLifecycle ?? null,
                liveWalSelection: (state as ControlWorkerRuntimeState & {
                    __debugLiveWalSelection?: unknown
                }).__debugLiveWalSelection ?? null,
                syntheticWalSnapshot: (state as ControlWorkerRuntimeState & {
                    __debugSyntheticWalSnapshot?: unknown
                }).__debugSyntheticWalSnapshot ?? null,
                syncPolicyLastRequest: (state as ControlWorkerRuntimeState & {
                    __debugSyncPolicyLastRequest?: unknown
                }).__debugSyncPolicyLastRequest ?? null,
                syncDriverLastQueue: (state as ControlWorkerRuntimeState & {
                    __debugSyncDriverLastQueue?: unknown
                }).__debugSyncDriverLastQueue ?? null,
                transportStage: (state as ControlWorkerRuntimeState & {
                    __debugTransportStageGetter?: (() => string | null) | undefined
                }).__debugTransportStageGetter?.() ?? null,
                transportDetails: (state as ControlWorkerRuntimeState & {
                    __debugTransportDetailsGetter?: (() => unknown) | undefined
                }).__debugTransportDetailsGetter?.() ?? null,
            }

            return snapshot
        },

        getPageCount(): number {
            return adapterState.state.workspaceBaseHead?.root?.meta?.pageCount ?? 0
        },
    }
}
